import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Clock, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    // Skip if user was passed from AuthCallback
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    const checkAuth = async () => {
      try {
        // Try cookie first, then token from localStorage
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: token 
            ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            : { 'Content-Type': 'application/json' }
        });

        if (!response.ok) throw new Error('No autenticado');

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  const handleLogout = async () => {
    // Clear token first
    localStorage.removeItem('token');
    
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {}
    
    // Full page reload to clear all state
    window.location.href = '/';
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary font-heading text-2xl">
          CARGANDO...
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // User not approved (and not admin)
  if (user && !user.is_approved && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-white/10">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
            <CardTitle className="font-heading text-2xl text-primary uppercase">
              Cuenta Pendiente de Aprobación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              Tu cuenta ha sido creada correctamente pero está pendiente de aprobación por un administrador.
            </p>
            <p className="text-muted-foreground">
              Una vez aprobada, podrás acceder a todas las funcionalidades de la aplicación.
            </p>
            <div className="pt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Registrado como: <span className="text-foreground">{user.email}</span>
              </p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full mt-4">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated and approved - pass user to children
  return children;
}
